import { Grid2, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format, isToday } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import { Pie, PieChart, ResponsiveContainer } from "recharts";

import { Widget, WidgetContent } from "components/elements/mui";
import { useFormatMessage } from "lang";
import {
  getLiveData,
  getSelectedPowerPlant,
  setShowLiveData,
  useAppDispatch,
  useAppSelector,
} from "store";
import { formatPower } from "utils/energy";

export const CurrentPower = () => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const liveData = useAppSelector(getLiveData);
  const power = useMemo(() => {
    if (!liveData) return formatPower(0);
    // Inverter-sourced plants (no meter) report the panels' DC input power,
    // which is what we want to show as the plant's current power. For
    // multi-inverter plants the backend aggregate already sums dcPower
    // across inverters. Meter-sourced plants keep the grid phase sum.
    const value =
      liveData.source === "inverter" && liveData.dcPower !== undefined
        ? liveData.dcPower
        : Object.values(liveData.power).reduce((sum, phase) => sum + phase, 0);
    return formatPower(value);
  }, [liveData]);
  const offline = selectedPowerPlant?.offline;
  const connectivity = useMemo(() => {
    const state = selectedPowerPlant?.connectivityState;
    if (!state || state === "ok") return null;
    const sinceDate = selectedPowerPlant?.connectivitySince ? new Date(selectedPowerPlant.connectivitySince) : null;
    const since = sinceDate ? format(sinceDate, isToday(sinceDate) ? "HH:mm" : "dd.MM HH:mm") : "";
    const device = selectedPowerPlant?.liveData?.source === "inverter" ? "inverter" : "meter";
    const id = state === "pi_offline" ? "connectivity.piOffline" : `connectivity.deviceOffline.${device}`;
    return formatMessage(id as Parameters<typeof formatMessage>[0], { values: { since } });
  }, [selectedPowerPlant, formatMessage]);
  const percentage = useMemo(
    () =>
      selectedPowerPlant?.capacity
        ? ((power.originalValue * 1000) / selectedPowerPlant.capacity) * 100
        : 0,
    [power.originalValue, selectedPowerPlant],
  );
  const displayPercentage = offline ? 0 : percentage;

  useEffect(() => {
    const observer = () => {
      const element = ref.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      dispatch(setShowLiveData(rect.top <= 0));
    };

    window.addEventListener("scroll", observer);
    return () => window.removeEventListener("scroll", observer);
  }, [dispatch]);

  return (
    <Widget
      ref={ref}
      title={formatMessage("currentPower")}
      icon="bolt"
      content={
        <Grid2 container spacing={2}>
          {selectedPowerPlant?.capacity && (
            <Grid2 container size={5} position={"relative"}>
              <ResponsiveContainer minHeight={56}>
                <PieChart>
                  <Pie
                    dataKey="value"
                    data={[
                      {
                        name: "Power",
                        value: displayPercentage,
                        fill: theme.palette.secondary.main,
                      },
                      {
                        name: "Remaining",
                        value: Math.max(100 - displayPercentage, 0),
                        fill: theme.palette.text.disabled,
                      },
                    ]}
                    startAngle={90}
                    endAngle={-270}
                    innerRadius={"83%"}
                    outerRadius={"115%"}
                    stroke="none"
                  />
                </PieChart>
              </ResponsiveContainer>
              <Typography
                variant="h6"
                align="center"
                color="primary"
                fontSize={12}
                fontWeight={700}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                {displayPercentage.toFixed(0)}%
              </Typography>
            </Grid2>
          )}
          <WidgetContent
            value={offline ? "—" : power.value}
            unit={offline ? "" : power.unit}
            gridProps={{ size: 7 }}
          />
          {connectivity && (
            <Grid2 size={12}>
              <Typography variant="caption" color="error" display="block" textAlign="center">
                {connectivity}
              </Typography>
            </Grid2>
          )}
        </Grid2>
      }
    />
  );
};
